import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { DarkPage } from "@/components/app/PageHeader";
import XeroProjectMappingPanel from "@/components/app/XeroProjectMappingPanel";
import {
  CheckCircle2, XCircle, ExternalLink, Copy, RotateCcw, Plug,
  ShieldCheck, AlertTriangle, Send, X, ChevronDown, ChevronRight,
} from "lucide-react";

const apiBase = process.env.REACT_APP_BACKEND_URL || "";

// ── Integration card ──────────────────────────────────────────────────────────

function IntegrationCard({ logo, name, description, category, status, children, envVars }) {
  const [expanded, setExpanded] = useState(false);
  const connected = status?.connected;
  return (
    <div className="bg-[#0d1117] border border-[#21262d] rounded-lg overflow-hidden">
      <button onClick={() => setExpanded(v => !v)} className="flex items-center gap-4 px-5 py-4 w-full text-left hover:bg-white/[0.02] transition-colors">
        <div className="w-10 h-10 rounded-lg bg-[#161b22] border border-[#30363d] flex items-center justify-center shrink-0 text-lg">
          {logo}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-white">{name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#30363d] text-[#7d8590]">{category}</span>
          </div>
          <p className="text-xs text-[#7d8590] mt-0.5 truncate">{description}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {connected && status?.sync_failed ? (
            <div
              className="flex items-center gap-1.5 text-xs text-amber-400"
              title={status?.sync_error || "The last sync attempt failed."}
            >
              <AlertTriangle className="w-3.5 h-3.5" /> Sync failed
            </div>
          ) : connected ? (
            <div className="flex items-center gap-1.5 text-xs text-[#00F5D4]">
              <CheckCircle2 className="w-3.5 h-3.5" />
              {status?.email || status?.company_name || status?.account_name || status?.org_name || "Connected"}
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-xs text-[#484f58]">
              <XCircle className="w-3.5 h-3.5" /> Not connected
            </div>
          )}
          <span className="p-1 text-[#7d8590]">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </span>
        </div>
      </button>
      {expanded && (
        <div className="border-t border-[#21262d] px-5 py-4 space-y-4">
          {envVars && envVars.length > 0 && (
            <div className="bg-[#161b22] border border-[#30363d] rounded-lg p-3">
              <div className="text-xs font-semibold text-[#7d8590] uppercase tracking-wider mb-2">Required env vars (Railway)</div>
              {envVars.map(v => (
                <code key={v} className="block text-xs text-[#00F5D4] font-mono mb-0.5">{v}</code>
              ))}
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}

// ── Connect button helpers ────────────────────────────────────────────────────

function OAuthButton({ href, label, connected, onDisconnect, disconnecting }) {
  if (connected) {
    return (
      <Button onClick={onDisconnect} disabled={disconnecting} variant="outline"
        className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs">
        {disconnecting ? "Disconnecting…" : "Disconnect"}
      </Button>
    );
  }
  return (
    <Button onClick={() => window.location.href = href} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold text-xs">
      <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Connect
    </Button>
  );
}

function WebhookInput({ label, placeholder, value, onChange, onSave, onTest, saving, connected }) {
  return (
    <div className="space-y-2">
      <label className="text-xs text-[#7d8590]">{label}</label>
      <div className="flex gap-2">
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#00F5D4]/50" />
        <Button onClick={onSave} disabled={saving} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold text-xs shrink-0">
          {saving ? "Saving…" : "Connect"}
        </Button>
        {connected && (
          <Button onClick={onTest} variant="outline" className="border-[#30363d] text-[#7d8590] text-xs shrink-0">
            <Send className="w-3.5 h-3.5 mr-1" /> Test
          </Button>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Integrations() {
  const { user } = useAuth();
  const isAdmin = user?.role === "owner" || user?.role === "admin";

  // Statuses
  const [statuses, setStatuses] = useState({});
  const [webhookConfig, setWebhookConfig] = useState(null);

  // Form state
  const [slackUrl, setSlackUrl] = useState("");
  const [teamsUrl, setTeamsUrl] = useState("");
  const [saving, setSaving] = useState({});
  const [disconnecting, setDisconnecting] = useState({});

  const load = useCallback(async () => {
    try {
      const calls = [
        api.get("/integrations/webhook-config").catch(() => null),
        api.get("/integrations/xero/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/slack/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/teams/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/google-calendar/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/outlook-calendar/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/quickbooks/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/docusign/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/google-drive/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/sharepoint/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/asana/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/monday/status").catch(() => ({ data: { connected: false } })),
        api.get("/integrations/hubspot/status").catch(() => ({ data: { connected: false } })),
      ];
      const [wh, xero, slack, teams, gcal, outlook, qb, ds, gdrive, sp, asana, monday, hubspot] = await Promise.all(calls);
      if (wh) setWebhookConfig(wh.data);
      setStatuses({
        xero: xero.data,
        slack: slack.data,
        teams: teams.data,
        gcal: gcal.data,
        outlook: outlook.data,
        qb: qb.data,
        docusign: ds.data,
        gdrive: gdrive.data,
        sharepoint: sp.data,
        asana: asana.data,
        monday: monday.data,
        hubspot: hubspot.data,
      });
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin or owner only");
    }
  }, []);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const connectHubspot = async () => {
    try {
      const { data } = await api.get("/integrations/hubspot/connect");
      if (data?.authorize_url) window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "HubSpot OAuth is not configured yet");
    }
  };

  const syncHubspotDeals = async () => {
    setSaving(v => ({ ...v, hubspot: true }));
    try {
      const { data } = await api.post("/integrations/hubspot/sync-deals");
      toast.success(`Synced ${data.synced} open deal${data.synced === 1 ? "" : "s"} from HubSpot`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSaving(v => ({ ...v, hubspot: false })); }
  };

  const backfillHubspot = async () => {
    setSaving(v => ({ ...v, hubspotBackfill: true }));
    try {
      const { data } = await api.post("/integrations/hubspot/backfill?days=365");
      toast.success(`Backfill complete — created ${data.processed} project${data.processed === 1 ? "" : "s"} from historical Closed-Won deals`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    finally { setSaving(v => ({ ...v, hubspotBackfill: false })); }
  };

  const backfillXero = async () => {
    setSaving(v => ({ ...v, xeroBackfill: true }));
    try {
      const { data } = await api.post("/integrations/xero/backfill");
      toast.success(`Checked ${data.checked} invoice${data.checked === 1 ? "" : "s"} — reconciled ${data.reconciled} as paid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Backfill failed"); }
    finally { setSaving(v => ({ ...v, xeroBackfill: false })); }
  };

  const disconnectHubspot = async () => {
    setDisconnecting(v => ({ ...v, hubspot: true }));
    try { await api.post("/integrations/hubspot/disconnect"); toast.success("HubSpot disconnected"); load(); }
    catch { toast.error("Failed to disconnect"); }
    finally { setDisconnecting(v => ({ ...v, hubspot: false })); }
  };

  const connectSlack = async () => {
    if (!slackUrl.trim()) return;
    setSaving(v => ({ ...v, slack: true }));
    try {
      await api.post("/integrations/slack/connect", { webhook_url: slackUrl });
      toast.success("Slack connected");
      setSlackUrl("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to connect Slack"); }
    finally { setSaving(v => ({ ...v, slack: false })); }
  };

  const testSlack = async () => {
    try { await api.post("/integrations/slack/test"); toast.success("Test message sent to Slack"); }
    catch { toast.error("Test failed"); }
  };

  const disconnectSlack = async () => {
    setDisconnecting(v => ({ ...v, slack: true }));
    try { await api.post("/integrations/slack/disconnect"); toast.success("Slack disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setDisconnecting(v => ({ ...v, slack: false })); }
  };

  const connectTeams = async () => {
    if (!teamsUrl.trim()) return;
    setSaving(v => ({ ...v, teams: true }));
    try {
      await api.post("/integrations/teams/connect", { webhook_url: teamsUrl });
      toast.success("Teams connected");
      setTeamsUrl("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to connect Teams"); }
    finally { setSaving(v => ({ ...v, teams: false })); }
  };

  const testTeams = async () => {
    try { await api.post("/integrations/teams/test"); toast.success("Test message sent to Teams"); }
    catch { toast.error("Test failed"); }
  };

  const disconnectTeams = async () => {
    setDisconnecting(v => ({ ...v, teams: true }));
    try { await api.post("/integrations/teams/disconnect"); toast.success("Teams disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setDisconnecting(v => ({ ...v, teams: false })); }
  };

  const disconnect = async (service, endpoint) => {
    setDisconnecting(v => ({ ...v, [service]: true }));
    try { await api.post(endpoint); toast.success(`${service} disconnected`); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setDisconnecting(v => ({ ...v, [service]: false })); }
  };

  const copyWebhook = () => {
    if (!webhookConfig) return;
    navigator.clipboard.writeText(`${apiBase}${webhookConfig.jira_url}`).then(() => toast.success("URL copied"));
  };

  const rotateToken = async () => {
    if (!window.confirm("Rotate the webhook token? All pasted URLs will need updating.")) return;
    try { await api.post("/integrations/webhook-config/rotate"); toast.success("Token rotated"); load(); }
    catch { toast.error("Rotation failed"); }
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="border-l-4 border-amber-500 bg-amber-500/10 p-5 rounded-r-lg text-sm text-amber-300">
          <AlertTriangle className="w-4 h-4 inline mr-2" />
          Integrations management is restricted to owners and admins.
        </div>
      </div>
    );
  }

  const INTEGRATIONS = [
    // ── Communication ──
    {
      logo: "💬", name: "Slack", category: "Communication",
      description: "Push task, invoice, and project health alerts to a Slack channel.",
      status: statuses.slack,
      envVars: [],
      content: (
        <div className="space-y-3">
          {statuses.slack?.connected ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#7d8590]">Webhook connected · channel: {statuses.slack?.channel || "default"}</span>
              <div className="flex gap-2">
                <Button onClick={testSlack} variant="outline" className="border-[#30363d] text-[#7d8590] text-xs">
                  <Send className="w-3.5 h-3.5 mr-1" /> Test
                </Button>
                <Button onClick={disconnectSlack} disabled={disconnecting.slack} variant="outline"
                  className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs">
                  {disconnecting.slack ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          ) : (
            <WebhookInput label="Slack incoming webhook URL" placeholder="https://hooks.slack.com/services/T.../B.../..."
              value={slackUrl} onChange={setSlackUrl} onSave={connectSlack} onTest={testSlack}
              saving={saving.slack} connected={statuses.slack?.connected} />
          )}
          <p className="text-xs text-[#484f58]">Create an incoming webhook at api.slack.com/apps → Your App → Incoming Webhooks.</p>
        </div>
      ),
    },
    {
      logo: "🟦", name: "Microsoft Teams", category: "Communication",
      description: "Send notifications to a Teams channel via incoming webhook.",
      status: statuses.teams,
      envVars: [],
      content: (
        <div className="space-y-3">
          {statuses.teams?.connected ? (
            <div className="flex items-center justify-between">
              <span className="text-xs text-[#7d8590]">Webhook connected</span>
              <div className="flex gap-2">
                <Button onClick={testTeams} variant="outline" className="border-[#30363d] text-[#7d8590] text-xs">
                  <Send className="w-3.5 h-3.5 mr-1" /> Test
                </Button>
                <Button onClick={disconnectTeams} disabled={disconnecting.teams} variant="outline"
                  className="border-rose-500/30 text-rose-400 hover:bg-rose-500/10 text-xs">
                  {disconnecting.teams ? "Disconnecting…" : "Disconnect"}
                </Button>
              </div>
            </div>
          ) : (
            <WebhookInput label="Teams incoming webhook URL" placeholder="https://yourorg.webhook.office.com/webhookb2/..."
              value={teamsUrl} onChange={setTeamsUrl} onSave={connectTeams} onTest={testTeams}
              saving={saving.teams} connected={statuses.teams?.connected} />
          )}
          <p className="text-xs text-[#484f58]">In Teams: channel → ··· → Connectors → Incoming Webhook → Configure.</p>
        </div>
      ),
    },
    // ── Calendar ──
    {
      logo: "📅", name: "Google Calendar", category: "Calendar",
      description: "Sync meetings and milestones to Google Calendar.",
      status: statuses.gcal,
      envVars: ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET", "GOOGLE_CALENDAR_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">{statuses.gcal?.connected ? `Connected as ${statuses.gcal.email}` : "Authorise access to your Google Calendar."}</span>
          <OAuthButton href={`${apiBase}/api/integrations/google-calendar/connect`} connected={statuses.gcal?.connected}
            onDisconnect={() => disconnect("gcal", "/integrations/google-calendar/disconnect")} disconnecting={disconnecting.gcal} />
        </div>
      ),
    },
    {
      logo: "📆", name: "Outlook / Microsoft 365", category: "Calendar",
      description: "Sync meetings and milestones to Outlook via Microsoft Graph.",
      status: statuses.outlook,
      envVars: ["MICROSOFT_CLIENT_ID", "MICROSOFT_CLIENT_SECRET", "MICROSOFT_TENANT_ID", "OUTLOOK_CALENDAR_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">{statuses.outlook?.connected ? `Connected as ${statuses.outlook.email}` : "Authorise access via Microsoft identity platform."}</span>
          <OAuthButton href={`${apiBase}/api/integrations/outlook-calendar/connect`} connected={statuses.outlook?.connected}
            onDisconnect={() => disconnect("outlook", "/integrations/outlook-calendar/disconnect")} disconnecting={disconnecting.outlook} />
        </div>
      ),
    },
    // ── Finance ──
    {
      logo: "🟢", name: "Xero", category: "Finance",
      description: "Push invoices and contacts to Xero. Payments sync back automatically.",
      status: statuses.xero,
      envVars: ["XERO_CLIENT_ID", "XERO_CLIENT_SECRET", "XERO_REDIRECT_URI"],
      content: (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#7d8590]">
              {statuses.xero?.connected ? `Connected — ${statuses.xero.org_name || "Xero org"}` : "Push invoices to Xero with one click."}
            </span>
            <div className="flex items-center gap-2">
              {statuses.xero?.connected && (
                <Button onClick={backfillXero} disabled={saving.xeroBackfill} variant="outline" className="text-xs">
                  {saving.xeroBackfill ? "Checking…" : "Backfill payment status"}
                </Button>
              )}
              <OAuthButton href={`${apiBase}/api/integrations/xero/connect`} connected={statuses.xero?.connected}
                onDisconnect={() => disconnect("xero", "/integrations/xero/disconnect")} disconnecting={disconnecting.xero} />
            </div>
          </div>
          {statuses.xero?.connected && (
            <p className="text-[11px] text-gray-400">
              "Backfill payment status" checks every invoice already pushed to Xero that CombinedWork doesn't yet show as paid, and catches up on any that were paid before this connection (or its webhook) was set up.
            </p>
          )}
          {statuses.xero?.connected && <XeroProjectMappingPanel />}
        </div>
      ),
    },
    {
      logo: "💚", name: "QuickBooks Online", category: "Finance",
      description: "Push invoices to QuickBooks Online. UK and US accounts supported.",
      status: statuses.qb,
      envVars: ["QUICKBOOKS_CLIENT_ID", "QUICKBOOKS_CLIENT_SECRET", "QUICKBOOKS_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">
            {statuses.qb?.connected ? `Connected — ${statuses.qb.company_name || "QB company"}` : "Authorise access to QuickBooks Online."}
          </span>
          <OAuthButton href={`${apiBase}/api/integrations/quickbooks/connect`} connected={statuses.qb?.connected}
            onDisconnect={() => disconnect("qb", "/integrations/quickbooks/disconnect")} disconnecting={disconnecting.qb} />
        </div>
      ),
    },
    // ── E-signature ──
    {
      logo: "✍️", name: "DocuSign", category: "E-signature",
      description: "Send contracts and client sign-off documents for e-signature.",
      status: statuses.docusign,
      envVars: ["DOCUSIGN_CLIENT_ID", "DOCUSIGN_CLIENT_SECRET", "DOCUSIGN_REDIRECT_URI", "DOCUSIGN_AUTH_SERVER (optional, defaults to demo)"],
      content: (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs text-[#7d8590]">
              {statuses.docusign?.connected ? `Connected — ${statuses.docusign.account_name || statuses.docusign.email}` : "Authorise DocuSign to send envelopes on your behalf."}
            </span>
            <OAuthButton href={`${apiBase}/api/integrations/docusign/connect`} connected={statuses.docusign?.connected}
              onDisconnect={() => disconnect("docusign", "/integrations/docusign/disconnect")} disconnecting={disconnecting.docusign} />
          </div>
          <p className="text-xs text-[#484f58]">Defaults to the DocuSign developer (demo) environment. Set DOCUSIGN_AUTH_SERVER=https://account.docusign.com for production.</p>
        </div>
      ),
    },
    // ── Storage ──
    {
      logo: "🟡", name: "Google Drive", category: "Storage",
      description: "Browse and attach Google Drive files to projects and documents.",
      status: statuses.gdrive,
      envVars: ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET", "GOOGLE_DRIVE_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">
            {statuses.gdrive?.connected ? `Connected as ${statuses.gdrive.email}` : "Read-only access to your Google Drive files."}
          </span>
          <OAuthButton href={`${apiBase}/api/integrations/google-drive/connect`} connected={statuses.gdrive?.connected}
            onDisconnect={() => disconnect("gdrive", "/integrations/google-drive/disconnect")} disconnecting={disconnecting.gdrive} />
        </div>
      ),
    },
    {
      logo: "🔷", name: "SharePoint / OneDrive", category: "Storage",
      description: "Browse and attach SharePoint and OneDrive files to projects.",
      status: statuses.sharepoint,
      envVars: ["MICROSOFT_CLIENT_ID", "MICROSOFT_CLIENT_SECRET", "MICROSOFT_TENANT_ID", "SHAREPOINT_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">
            {statuses.sharepoint?.connected ? `Connected as ${statuses.sharepoint.email}` : "Authorise read access via Microsoft Graph."}
          </span>
          <OAuthButton href={`${apiBase}/api/integrations/sharepoint/connect`} connected={statuses.sharepoint?.connected}
            onDisconnect={() => disconnect("sharepoint", "/integrations/sharepoint/disconnect")} disconnecting={disconnecting.sharepoint} />
        </div>
      ),
    },
    // ── Project import ──
    {
      logo: "🟠", name: "Asana", category: "Project import",
      description: "Import Asana projects and tasks. Receive task-done webhooks.",
      status: statuses.asana,
      envVars: ["ASANA_CLIENT_ID", "ASANA_CLIENT_SECRET", "ASANA_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">
            {statuses.asana?.connected ? `Connected as ${statuses.asana.email || statuses.asana.name}` : "Authorise access to import Asana projects."}
          </span>
          <OAuthButton href={`${apiBase}/api/integrations/asana/connect`} connected={statuses.asana?.connected}
            onDisconnect={() => disconnect("asana", "/integrations/asana/disconnect")} disconnecting={disconnecting.asana} />
        </div>
      ),
    },
    {
      logo: "🔴", name: "Monday.com", category: "Project import",
      description: "Import Monday.com boards as projects and tasks.",
      status: statuses.monday,
      envVars: ["MONDAY_CLIENT_ID", "MONDAY_CLIENT_SECRET", "MONDAY_REDIRECT_URI"],
      content: (
        <div className="flex items-center justify-between">
          <span className="text-xs text-[#7d8590]">
            {statuses.monday?.connected ? `Connected — ${statuses.monday.account_name || statuses.monday.email}` : "Authorise access to import Monday.com boards."}
          </span>
          <OAuthButton href={`${apiBase}/api/integrations/monday/connect`} connected={statuses.monday?.connected}
            onDisconnect={() => disconnect("monday", "/integrations/monday/disconnect")} disconnecting={disconnecting.monday} />
        </div>
      ),
    },
    // ── Existing ──
    {
      logo: "🔵", name: "Jira", category: "Project import",
      description: "When a Jira issue transitions to Done, CombinedWork logs the time and runs the margin guardrail.",
      status: { connected: !!webhookConfig },
      envVars: [],
      content: (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-[#161b22] border border-[#30363d] text-[#00F5D4] font-mono text-xs px-3 py-2 rounded-md break-all">
              {webhookConfig ? `${apiBase}${webhookConfig.jira_url}` : "Loading…"}
            </code>
            <Button onClick={copyWebhook} variant="outline" className="border-[#30363d] text-[#7d8590] text-xs shrink-0">
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy
            </Button>
            <Button onClick={rotateToken} variant="outline" className="border-[#30363d] text-[#7d8590] text-xs shrink-0">
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Rotate
            </Button>
          </div>
          <p className="text-xs text-[#484f58]">Atlassian: Settings → System → Webhooks → Create. Trigger on <strong className="text-[#7d8590]">issue updated</strong>, scope to relevant project.</p>
        </div>
      ),
    },
    {
      logo: "🟠", name: "HubSpot", category: "CRM",
      description: "Connect via OAuth, sync your open pipeline, and feed deal win-probability into Predictions. Closed-Won deals auto-create projects.",
      status: statuses.hubspot || { connected: false },
      envVars: ["HUBSPOT_OAUTH_CLIENT_ID", "HUBSPOT_OAUTH_CLIENT_SECRET", "HUBSPOT_OAUTH_REDIRECT_URI"],
      content: (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {statuses.hubspot?.connected ? (
              <>
                <Button onClick={syncHubspotDeals} disabled={saving.hubspot} className="bg-violet-600 text-white hover:bg-violet-700 font-semibold text-xs">
                  {saving.hubspot ? "Syncing…" : "Sync open deals"}
                </Button>
                <Button onClick={backfillHubspot} disabled={saving.hubspotBackfill} variant="outline" className="text-xs">
                  {saving.hubspotBackfill ? "Backfilling…" : "Backfill historical deals"}
                </Button>
                <Button onClick={disconnectHubspot} disabled={disconnecting.hubspot} variant="outline" className="text-xs">
                  Disconnect
                </Button>
              </>
            ) : (
              <Button onClick={connectHubspot} className="bg-violet-600 text-white hover:bg-violet-700 font-semibold text-xs">
                Connect with HubSpot
              </Button>
            )}
          </div>
          {statuses.hubspot?.last_synced_at && (
            <p className="text-[11px] text-gray-400">
              Last sync: {new Date(statuses.hubspot.last_synced_at).toLocaleString()} · {statuses.hubspot.synced_deals || 0} deals
            </p>
          )}
          <p className="text-[11px] text-gray-400">
            OAuth needs <strong>HUBSPOT_OAUTH_CLIENT_ID/SECRET/REDIRECT_URI</strong> in Railway. Open deals sync hourly and on demand, feeding the Predictions page. A private-app <strong>HUBSPOT_API_KEY</strong> also works as a fallback.
          </p>
        </div>
      ),
    },
  ];

  const categories = [...new Set(INTEGRATIONS.map(i => i.category))];
  const connectedCount = Object.values(statuses).filter(s => s?.connected).length;

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Integrations</h1>
        <p className="text-sm text-[#7d8590] mt-1">
          {connectedCount} of {INTEGRATIONS.length} integrations connected
        </p>
        <div className="mt-3 h-1.5 bg-[#21262d] rounded-full overflow-hidden w-64">
          <div className="h-full bg-[#00F5D4] rounded-full transition-all" style={{ width: `${(connectedCount / INTEGRATIONS.length) * 100}%` }} />
        </div>
      </div>

      {categories.map(cat => (
        <div key={cat} className="mb-8">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-[#7d8590] mb-3">{cat}</h2>
          <div className="space-y-3">
            {INTEGRATIONS.filter(i => i.category === cat).map(integration => (
              <IntegrationCard key={integration.name} {...integration} envVars={integration.envVars} status={integration.status}>
                {integration.content}
              </IntegrationCard>
            ))}
          </div>
        </div>
      ))}
    </div></DarkPage>
  );
}
